import { useCallback, useRef, useState } from 'react';

export type StaleSafeLoadHandlers<T> = {
  onSuccess: (data: T) => void;
  onError?: (error: unknown) => void;
  /**
   * When true, only the current generation may set `loadFailed` on error and
   * clear it on success. Leave off when the screen surfaces failures another
   * way (e.g. a toast) and does not render a load-failed banner.
   */
  trackLoadFailed?: boolean;
};

/**
 * Monotonic request-id guard for async list loads: only the newest fetch may
 * update `isLoading` / optional `loadFailed` (and whatever the caller writes in
 * `onSuccess` / `onError`).
 *
 * Call `invalidate()` when a scope change must retire in-flight work — e.g.
 * clearing the clinic filter so a late response cannot refill an emptied table.
 */
export function useStaleSafeLoader() {
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const requestIdRef = useRef(0);

  const invalidate = useCallback(() => {
    requestIdRef.current += 1;
    setIsLoading(false);
  }, []);

  const run = useCallback(
    async <T>(
      execute: () => Promise<T>,
      handlers: StaleSafeLoadHandlers<T>
    ): Promise<boolean> => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      // A load the caller has already moved on from must not write anything.
      const isCurrent = () => requestIdRef.current === requestId;
      setIsLoading(true);
      try {
        const data = await execute();
        if (!isCurrent()) return false;
        if (handlers.trackLoadFailed) {
          setLoadFailed(false);
        }
        handlers.onSuccess(data);
        return true;
      } catch (error) {
        if (!isCurrent()) return false;
        if (handlers.trackLoadFailed) {
          setLoadFailed(true);
        }
        handlers.onError?.(error);
        return false;
      } finally {
        // The spinner belongs to the newest load: a superseded one leaves it
        // alone rather than reporting itself finished.
        if (isCurrent()) {
          setIsLoading(false);
        }
      }
    },
    []
  );

  return { isLoading, loadFailed, run, invalidate };
}
